import { Container, Paper, Button, Box } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import PictureInput from 'Components/Input/PictureInput';
import KeywordsForm from 'Components/KeywordsForm';
import LayoutWithPageLoading from 'Components/layout/LayoutWithPageLoading';
import TagsListSelectionForm from 'Features/Tags/TagsListSelectionForm';
import { Formik } from 'formik';
import useAsyncTask from 'hooks/useAsyncTask';
import { IReactFormProps, MLFormContent } from 'react-forms';
import { DefinitionsModel } from 'Models/Definitions';
import { Definition, DefinitionState } from 'Models/Definitions/@types';
import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { ReduxStore } from 'stores';
import { definitionFormToRequestDataMapping } from 'Models/Definitions/definitionParser';
import Loader from 'Components/Loader';
import useToastMessage from 'hooks/useToastMessage';
import { TDispatch } from 'Typings/Global';
import HeaderActionButtons from 'Components/Buttons/HeaderActionButtons';
import useConfirmationDialog from 'hooks/useConfirmationDialog';
import { Editor } from '@tinymce/tinymce-react';
import EditorDetails from 'Components/Editor';
import { TINY_API_KEY } from 'Resources/Config';

type StoreSelection = Pick<DefinitionState, 'definition'>;
export interface DefinitionDetailsProps extends RouteComponentProps<{ id: string }> { }

const DefinitionDetails: FC<DefinitionDetailsProps> = ({ match, history }) => {
    const classes = useStyles();

    const id = match.params['id'];

    const dispatch = useDispatch<TDispatch>();

    const { definition } = useSelector<ReduxStore, StoreSelection>(({ Definitions: { definition } }) => ({ definition }));

    const getDataTask = useAsyncTask(async () => dispatch(DefinitionsModel.getItem(id, { filter: { include: ['tags'] } })));
    const togglePublishTask = useAsyncTask(async () => await dispatch(DefinitionsModel.postActivity(DefinitionsModel.Activities.TOGGLE_PUBLISH, {}, { id: definition?.id, publish: !definition?.isPublished })))
    const deleteTask = useAsyncTask(async () => await dispatch(DefinitionsModel.deleteItem(definition!.id, {}, undefined, { definition })));

    const updateTask = useAsyncTask(async (formData: Partial<Definition> & { id: string }) => {
        await dispatch(DefinitionsModel.patchItem(formData.id, formData))
        await dispatch(DefinitionsModel.getItem(id))
    });

    const withToast = useToastMessage();
    const withConfirmationDialog = useConfirmationDialog();

    useEffect(() => {
        getDataTask.run();
        // eslint-disable-next-line
    }, []);

    const handleSubmit = (formData: Partial<Definition>) => {

        formData.tagIds = formData.tags?.map((t) => t.id);

        const temp = document.createElement('div');
        temp.innerHTML = formData.fullDescription || '';
        const hTags = ["h1", "h2", "h3", "h4", "h5", "h6"];
        const elements = temp.querySelectorAll<HTMLElement>(hTags.join());
        elements.forEach(el => {
            const text = el.innerText;
            const id = text.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)?.map(x => x.toLowerCase()).join('-')!;
            el.setAttribute("id", id);
            const anchor = document.createElement('a');
            anchor.href = `#${id}`;
            anchor.innerText = text;
            el.innerHTML = "";
            el.appendChild(anchor);
        });

        formData.fullDescription = temp.innerHTML;

        formData = definitionFormToRequestDataMapping(formData);
        withToast(async () => {
            if (formData.id) {
                await updateTask.run({ id: formData.id, ...formData });
            }
        })
    }

    const togglePublish = () => {
        if (definition?.id) {
            withToast(togglePublishTask.run, { successToastMessage: definition.isPublished ? "Unpublished successfully" : "Published successfully" });
        }
    }

    const deleteDefinition = () => {
        withConfirmationDialog(async () => {
            await deleteTask.run();
            history.replace('/dashboard/techtionary');
        }, { agreeText: 'Delete', cancelText: 'Cancel', message: 'Do you want to delete this book?' }, { successToastMessage: "Delete Success" })
    }

    const FormConfig: IReactFormProps['config'] = [
        {
            type: "text",
            valueKey: 'shortDescription',
            fieldProps: {
                fullWidth: true,
                multiline: true,
                label: "Short Description"
            }
        }
    ]

    const updating = updateTask.status === "PROCESSING";

    return (
        <LayoutWithPageLoading loading={getDataTask.status === 'PROCESSING'} assertedItems={[definition]} >
            <HeaderActionButtons items={[
                { icon: definition?.isPublished ? 'lock_open' : 'lock', onClick: togglePublish },
                { icon: 'delete', onClick: deleteDefinition }
            ]} />
            <Formik<Partial<Definition>>
                initialValues={definition || {}}
                onSubmit={handleSubmit}
            >
                {
                    formikProps => (
                        <Container className={classes.container}>
                            <Button disabled={updating} type="submit" onSubmit={formikProps.submitForm} onClick={formikProps.submitForm} className={classes.fixedButton} color={'primary'} variant={'contained'} size={'large'} >
                                {updating ? <Loader size={'xs'} /> : 'SAVE'}
                            </Button>
                            <div className={clsx(classes.metaContainer)} >
                                <Paper className={clsx(classes.formContainer, classes.pictureInputContainer)} >
                                    <MLFormContent
                                        formId={'meta-form'}
                                        formikProps={formikProps}
                                        settings={{ verticalSpacing: 16 }}
                                        schema={[
                                            {
                                                type: 'text',
                                                valueKey: 'title',
                                                fieldProps: {
                                                    fullWidth: true,
                                                    label: 'Title'
                                                }
                                            },
                                            {
                                                type: 'text',
                                                valueKey: 'slug',
                                                fieldProps: {
                                                    fullWidth: true,
                                                    label: 'Slug'
                                                }
                                            },
                                            {
                                                type: 'text',
                                                valueKey: 'ogTitle',
                                                fieldProps: {
                                                    fullWidth: true,
                                                    label: 'OG Title',
                                                    multiline: true
                                                }
                                            },
                                        ]}
                                    />
                                    <PictureInput
                                        withCropping
                                        cropConfig={{ aspect: 1 }}
                                        initialPicture={definition?.picture}
                                        onPicUpload={pic => formikProps.setFieldValue('picture', pic)}
                                        onPictureRemove={() => formikProps.setFieldValue("picture", undefined)}
                                    />
                                    <Box my={2} >
                                        <KeywordsForm
                                            initialKeywords={formikProps.values['keywords']}
                                            textFieldProps={{ label: "Keywords", fullWidth: true }}
                                            chipProps={{ size: 'small' }}
                                            onKeywordsUpdate={keywords => formikProps.setFieldValue('keywords', keywords)}
                                        />
                                    </Box>
                                    <TagsListSelectionForm initialTags={definition?.tags || []} onChange={tags => formikProps.setFieldValue('tags', tags)} />
                                </Paper>
                            </div>
                            <Box flex={1} >
                                <Paper className={clsx(classes.formContainer)} >
                                    <Editor
                                        apiKey={TINY_API_KEY}
                                        init={{
                                            minHeight: '50px !important',
                                            toolbar: 'bold link italic code undo redo',
                                            plugins: ['link']
                                        }}
                                        initialValue={definition?.definition || ''}
                                        onChange={e => formikProps.setFieldValue('definition', e.target.getContent())}
                                        inline
                                    />
                                </Paper>
                                <Paper className={clsx(classes.contentContainer, classes.formContainer)} >
                                    <MLFormContent
                                        formId={'content-form'}
                                        formikProps={formikProps}
                                        settings={{ verticalSpacing: 16 }}
                                        schema={FormConfig}
                                    />
                                </Paper>
                                <EditorDetails
                                    initialValue={definition?.fullDescription}
                                    onChangeValue={(e: any) => formikProps.setFieldValue('fullDescription', e.target.getContent())}
                                />
                            </Box>
                        </Container>
                    )
                }
            </Formik>
        </LayoutWithPageLoading>
    )
}

const useStyles = makeStyles<Theme>((theme) => {
    return (createStyles({
        container: {
            display: 'flex',
            // padding: '88px 0px'
        },
        metaContainer: {
            width: 320,
            marginRight: 16,
        },
        formContainer: {
            padding: 24,
            borderRadius: 4,
            marginBottom: 16
        },
        contentContainer: {
            flex: 1
        },
        pictureInputContainer: {
            padding: 32,
        },
        fixedButton: {
            position: 'fixed',
            top: 64 + 24,
            right: 24
        }
    }))
})

export default DefinitionDetails